import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { SEARCH_PRODUCTS } from '../../apollo/queries/productQueries';
import ImageWithPlaceholder from '../../components/ImageWithPlaceholder';
import '../css/SearchResultsPage.css';

const ProductCard = ({ product, onCartClick, onImageClick }) => {
    // 이미지 URL을 안전하게 파싱하는 함수 (ShopPage와 동일한 로직)
    const getImageUrl = (images) => {
        if (!images) {
            return null;
        }
        
        if (Array.isArray(images)) {
            return images[0] || null;
        }
        
        if (typeof images === 'string') {
            // 빈 문자열이나 "[]" 체크
            if (images.trim() === '' || images.trim() === '[]') {
                return null;
            }
            
            const trimmedImages = images.trim();
            
            // 먼저 URL 패턴을 찾아서 추출 (JSON 파싱 전에 시도)
            const urlPattern = /https?:\/\/[^\s,\]"'}]+/g;
            const urlMatches = trimmedImages.match(urlPattern);
            
            if (urlMatches && urlMatches.length > 0) {
                // 첫 번째 완전한 URL을 반환
                return urlMatches[0];
            }
            
            // 단순한 URL 문자열인 경우
            if (!trimmedImages.startsWith('[') && !trimmedImages.startsWith('"')) {
                // 상대 경로나 단순 URL인 경우
                if (trimmedImages.includes('/') || trimmedImages.includes('http')) {
                    return trimmedImages;
                }
                return trimmedImages;
            }
            
            // JSON 파싱 시도 (완전한 JSON인 경우만)
            try {
                // JSON 문자열이 완전한지 확인
                if (trimmedImages.startsWith('[') && trimmedImages.endsWith(']')) {
                    const parsedImages = JSON.parse(trimmedImages);
                    if (Array.isArray(parsedImages) && parsedImages.length > 0 && parsedImages[0]) {
                        return parsedImages[0];
                    }
                } else if (trimmedImages.startsWith('"') && trimmedImages.endsWith('"')) {
                    const parsedImages = JSON.parse(trimmedImages);
                    if (typeof parsedImages === 'string' && parsedImages.trim() !== '') {
                        return parsedImages;
                    }
                }
            } catch (error) {
                // JSON 파싱 실패 시 더 이상 에러를 로그하지 않음
                // 이미 위에서 URL을 찾았으므로 여기서 실패해도 괜찮음
            }
            
            // 위의 모든 방법이 실패한 경우, 문자열에서 URL 부분만 추출
            const partialUrlMatch = trimmedImages.match(/https?:\/\/[^"'\s}]+/);
            if (partialUrlMatch) {
                return partialUrlMatch[0];
            }
            
            // 상대 경로 추출
            const pathMatch = trimmedImages.match(/\/[^"'\s}]+/);
            if (pathMatch) {
                return pathMatch[0];
            }
        }
        
        return null;
    };

    const imageUrl = getImageUrl(product.images);

    return (
        <div className="srp-search-product-card">
            <div className="srp-search-product-image-container" onClick={() => onImageClick(product)}>
                <ImageWithPlaceholder 
                    src={imageUrl} 
                    alt={product.name}
                    className="srp-search-product-image"
                />
            </div>
            <div className="srp-search-product-info">
                <h4 className="srp-search-product-name">{product.name}</h4>
                <div className="srp-search-product-details">
                    <p><strong>상품 코드:</strong> {product.productCode || '미입력'}</p>
                    <p><strong>사이즈:</strong> {product.size || '미입력'}</p>
                    {product.options && product.options.length > 0 && (
                        <p><strong>옵션:</strong> {product.options.join(', ')}</p>
                    )}
                </div>
                {product.description && <p className="srp-search-product-description">{product.description}</p>}
            </div>
            <div className="srp-search-product-actions">
                <button 
                    className="srp-search-cart-button"
                    onClick={() => onCartClick(product)}
                    disabled={product.stock === 0}
                >
                    장바구니
                    <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 0 24 24" width="20px" fill="currentColor">
                        <path d="M0 0h24v24H0V0z" fill="none"/>
                        <path d="M7 18c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12L8.1 13h7.45c.75 0 1.41-.41 1.75-1.03L21.7 4H5.21l-.94-2H1zm16 16c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
                    </svg>
                </button>
            </div>
        </div>
    );
};

const SearchResultsPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const searchQuery = searchParams.get('q') || '';
    
    // 페이지네이션 파라미터 읽기
    const pageParam = parseInt(searchParams.get('page') || '1', 10);
    const itemsPerPageParam = parseInt(searchParams.get('limit') || '18', 10);
    
    // 페이지네이션 상태
    const [currentPage, setCurrentPage] = useState(pageParam);
    const [itemsPerPage, setItemsPerPage] = useState(itemsPerPageParam);
    
    // 검색 쿼리 변수 설정 (useMemo로 최적화)
    const searchQueryVariables = useMemo(() => {
        return {
            query: searchQuery,
            limit: itemsPerPage,
            offset: (currentPage - 1) * itemsPerPage
        };
    }, [searchQuery, currentPage, itemsPerPage]);
    
    // GraphQL 쿼리 실행
    const { data, loading, error } = useQuery(SEARCH_PRODUCTS, {
        variables: searchQueryVariables,
        skip: !searchQuery || searchQuery.length < 2,
        errorPolicy: 'all'
    });

    // URL 파라미터에서 페이지네이션 정보 읽기
    useEffect(() => {
        const page = parseInt(searchParams.get('page') || '1', 10);
        const limit = parseInt(searchParams.get('limit') || '18', 10);
        setCurrentPage(page);
        setItemsPerPage(limit);
    }, [searchParams]);
    
    // 검색어 변경 시 첫 페이지로 리셋
    useEffect(() => {
        if (searchQuery && searchQuery.length >= 2) {
            setCurrentPage(1);
            const params = new URLSearchParams(searchParams);
            params.set('page', '1');
            setSearchParams(params, { replace: true });
        }
    }, [searchQuery]);

    const products = data?.searchProducts?.products || [];
    const totalProducts = data?.searchProducts?.totalCount || 0;

    const handleProductClick = (product) => {
        navigate(`/product?id=${product.id}`);
    };

    const handleImageClick = (product) => {
        navigate(`/product?id=${product.id}`);
    };

    const handleCartClick = (product) => {
        // 여기에 장바구니 추가 로직을 구현할 수 있습니다
        console.log('Add to cart:', product);
        alert(`${product.name}을(를) 장바구니에 추가했습니다!`);
    };
    
    // 페이지네이션 계산
    const totalPages = Math.ceil(totalProducts / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = Math.min(startIndex + itemsPerPage, totalProducts);
    
    // 페이지 번호 생성 (최대 10개 표시)
    const getPageNumbers = () => {
        const pages = [];
        const maxPagesToShow = 10;
        let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
        let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
        
        if (endPage - startPage < maxPagesToShow - 1) {
            startPage = Math.max(1, endPage - maxPagesToShow + 1);
        }
        
        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }
        return pages;
    };
    
    // 페이지네이션 핸들러
    const handlePageChange = useCallback((newPage) => {
        setCurrentPage(newPage);
        const params = new URLSearchParams(searchParams);
        params.set('page', newPage.toString());
        setSearchParams(params, { replace: true });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [searchParams, setSearchParams]);
    
    // 아이템 개수 변경 핸들러
    const handleItemsPerPageChange = useCallback((newItemsPerPage) => {
        setItemsPerPage(newItemsPerPage);
        setCurrentPage(1);
        const params = new URLSearchParams(searchParams);
        params.set('limit', newItemsPerPage.toString());
        params.set('page', '1');
        setSearchParams(params, { replace: true });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [searchParams, setSearchParams]);

    if (!searchQuery) {
        return (
            <div className="srp-search-results-page">
                <div className="srp-search-container">
                    <h1>검색 결과</h1>
                    <p className="srp-no-query-message">검색어를 입력해주세요.</p>
                </div>
            </div>
        );
    }

    if (searchQuery.length < 2) {
        return (
            <div className="srp-search-results-page">
                <div className="srp-search-container">
                    <h1>검색 결과</h1>
                    <p className="srp-error-message">검색어는 2자 이상 입력해주세요.</p>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="srp-search-results-page">
                <div className="srp-search-container">
                    <h1>검색 결과</h1>
                    <div className="srp-loading-message">
                        <p>검색 중...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="srp-search-results-page">
                <div className="srp-search-container">
                    <h1>검색 결과</h1>
                    <p className="srp-error-message">
                        검색 중 오류가 발생했습니다: {error.message}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="srp-search-results-page">
            <div className="srp-search-container">
                <div className="srp-search-header">
                    <h1>검색 결과</h1>
                    <div className="srp-search-info">
                        <span className="srp-search-query">"{searchQuery}"</span>에 대한 검색 결과
                        <div className="srp-search-header-right">
                            <span className="srp-search-count">총 {totalProducts}개의 상품</span>
                            <div className="srp-items-per-page-selector">
                                <select 
                                    id="srp-items-per-page"
                                    value={itemsPerPage} 
                                    onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value, 10))}
                                    className="srp-items-per-page-select"
                                >
                                    <option value="18">18개</option>
                                    <option value="36">36개</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>
                
                {products.length > 0 || totalProducts > 0 ? (
                    <>
                        <div className="srp-search-results-grid">
                            {products.map((product) => (
                                <ProductCard 
                                    key={product.id} 
                                    product={product} 
                                    onCartClick={handleCartClick} 
                                    onImageClick={handleImageClick} 
                                />
                            ))}
                        </div>
                        {totalPages > 1 && (
                            <div className="srp-pagination-container">
                                <div className="srp-pagination">
                                    <button 
                                        className="srp-pagination-btn"
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={currentPage === 1}
                                        aria-label="이전 페이지"
                                    >
                                        ‹
                                    </button>
                                    <div className="srp-pagination-numbers">
                                        {currentPage > 5 && (
                                            <>
                                                <button 
                                                    className="srp-pagination-number"
                                                    onClick={() => handlePageChange(1)}
                                                >
                                                    1
                                                </button>
                                                {currentPage > 6 && <span className="srp-pagination-ellipsis">...</span>}
                                            </>
                                        )}
                                        {getPageNumbers().map((pageNum) => (
                                            <button
                                                key={pageNum}
                                                className={`srp-pagination-number ${pageNum === currentPage ? 'active' : ''}`}
                                                onClick={() => handlePageChange(pageNum)}
                                            >
                                                {pageNum}
                                            </button>
                                        ))}
                                        {currentPage < totalPages - 4 && (
                                            <>
                                                {currentPage < totalPages - 5 && <span className="srp-pagination-ellipsis">...</span>}
                                                <button 
                                                    className="srp-pagination-number"
                                                    onClick={() => handlePageChange(totalPages)}
                                                >
                                                    {totalPages}
                                                </button>
                                            </>
                                        )}
                                    </div>
                                    <button 
                                        className="srp-pagination-btn"
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                        aria-label="다음 페이지"
                                    >
                                        ›
                                    </button>
                                </div>
                                <div className="srp-pagination-info">
                                    {startIndex + 1}-{endIndex} / {totalProducts}
                                </div>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="srp-no-results">
                        <h2>검색 결과가 없습니다</h2>
                        <p>"{searchQuery}"에 대한 상품을 찾을 수 없습니다.</p>
                        <div className="srp-search-suggestions">
                            <h3>검색 팁:</h3>
                            <ul>
                                <li>검색어의 철자가 정확한지 확인해보세요</li>
                                <li>더 일반적인 검색어를 사용해보세요</li>
                                <li>다른 키워드를 시도해보세요</li>
                            </ul>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SearchResultsPage; 